import React, { useState } from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { AuditedStatementSlice, ReportData } from '../tokens/types';
import { PdfSection } from '../primitives/PdfPrimitives';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';

const BS_SECTIONS: Array<{ name: string; match: RegExp }> = [
    { name: 'Equity & Liabilities', match: /equity|liabilit|borrowing|payab|provis|reserv|share/i },
    { name: 'Assets', match: /asset|receiv|invent|cash|invest|intang|tangib|advance|deposit/i },
];

const isBalanceSheet = (name: string) => /balance\s*sheet/i.test(name);
const isPnL = (name: string) => /p\s*&\s*l|profit|income/i.test(name);
const isRatios = (name: string) => /ratio/i.test(name);

const groupBalanceSheet = (rows: AuditedStatementSlice['rows']) => {
    const groups: Record<string, typeof rows> = {};
    rows.forEach((r) => {
        const bucket = BS_SECTIONS.find((s) => s.match.test((r.label ?? '') + ' ' + r.classificationName))?.name ?? 'Other';
        (groups[bucket] ??= []).push(r);
    });
    return groups;
};

const formatCell = (v?: string) => (v == null || v === '' ? '—' : v);

const yearColumns = (slice: AuditedStatementSlice) =>
    slice.headers.filter((h) => /^\d{4}$|^FY/i.test(h.key) || h.key !== 'classificationName').slice(-6);

// ──────────────────────────── PDF ────────────────────────────

// Move aggregated ("Total …") rows to the bottom of each group so the running
// figures read first and the summary lands last — easier to scan. Stable sort:
// non-aggregated keep their relative order, aggregated rows are appended.
const totalsLast = <T extends { isAggregated?: boolean }>(rows: T[]): T[] =>
    [...rows].sort((a, b) => Number(!!a.isAggregated) - Number(!!b.isAggregated));

const PdfStatementTable: React.FC<{ slice: AuditedStatementSlice }> = ({ slice }) => {
    const cols = yearColumns(slice);
    const useGroups = isBalanceSheet(slice.name);
    const rawGroups = useGroups ? groupBalanceSheet(slice.rows) : { [slice.name]: slice.rows };
    const groups = Object.fromEntries(Object.entries(rawGroups).map(([k, v]) => [k, totalsLast(v)]));
    return (
        <View style={{ marginTop: space.sm, borderTopWidth: 0.5, borderTopColor: colors.ink }}>
            <View
                style={{
                    flexDirection: 'row',
                    paddingVertical: 5,
                    borderBottomWidth: 0.5,
                    borderBottomColor: colors.ink,
                    backgroundColor: colors.surfaceAlt,
                }}
                fixed
            >
                <Text
                    style={{
                        width: '34%',
                        fontSize: typeScale.micro,
                        fontFamily: fontFamily.sansBold,
                        color: colors.muted,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        paddingHorizontal: 4,
                    }}
                >
                    Classification
                </Text>
                {cols.map((h) => (
                    <Text
                        key={h.key}
                        style={{
                            flex: 1,
                            fontSize: typeScale.micro,
                            fontFamily: fontFamily.sansBold,
                            color: colors.muted,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            textAlign: 'right',
                            paddingHorizontal: 4,
                        }}
                    >
                        {h.name}
                    </Text>
                ))}
            </View>
            {Object.entries(groups).map(([groupName, rows]) => (
                <View key={groupName}>
                    {useGroups ? (
                        <View
                            style={{ backgroundColor: colors.surface, paddingHorizontal: 4, paddingVertical: 4 }}
                            wrap={false}
                            minPresenceAhead={50}
                        >
                            <Text
                                style={{
                                    fontSize: typeScale.micro,
                                    fontFamily: fontFamily.sansBold,
                                    // Per Figma 32:100991 — table sub-group headers (EQUITY & LIABILITIES,
                                    // ASSETS) render in ink/black on a light beige strip, NOT in brand purple.
                                    color: colors.ink00,
                                    letterSpacing: 1,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {groupName}
                            </Text>
                        </View>
                    ) : null}
                    {rows.map((r, i) => (
                        <View
                            key={`${groupName}-${i}`}
                            style={{
                                flexDirection: 'row',
                                paddingVertical: 4,
                                borderBottomWidth: 0.25,
                                borderBottomColor: colors.hairlineSoft,
                                backgroundColor: r.isAggregated ? colors.surfaceAlt : undefined,
                            }}
                            wrap={false}
                        >
                            <Text
                                style={{
                                    width: '34%',
                                    fontSize: typeScale.caption,
                                    color: colors.inkSoft,
                                    // Aggregated rows already get the surfaceAlt strip
                                    // behind them — sansBold on top reads as too heavy
                                    // (per design feedback). Using sans@600 instead so
                                    // the row is still emphasised but doesn't shout.
                                    fontFamily: fontFamily.sans,
                                    fontWeight: r.isAggregated ? 600 : 400,
                                    paddingLeft: r.subLabel ? 8 : 4,
                                    paddingRight: 4,
                                }}
                            >
                                {r.classificationName}
                            </Text>
                            {cols.map((h) => (
                                <Text
                                    key={h.key}
                                    style={{
                                        flex: 1,
                                        fontSize: typeScale.caption,
                                        color: colors.ink,
                                        fontFamily: fontFamily.sans,
                                        textAlign: 'right',
                                        paddingHorizontal: 4,
                                    }}
                                >
                                    {formatCell(r.values[h.key])}
                                </Text>
                            ))}
                        </View>
                    ))}
                </View>
            ))}
        </View>
    );
};

const PdfAuditedStatements: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    // Fall through to provisional statements when audited slices aren't present
    // — same shape, same renderer, just labeled differently in the subtitle so the
    // reader knows the numbers are management-supplied rather than CA-audited.
    const audited = data.auditedStatements;
    const provisional = data.provisionalStatements;
    const isProvisional = !audited || audited.statements.length === 0;
    const as = isProvisional ? provisional : audited;
    if (!as || as.statements.length === 0) return null;
    // Subtitle drops the "Default view" fallback — when the API doesn't tag the
    // dataset as Standalone or Consolidated we just show the statement count and
    // let the slice headings carry the rest. Showing a "Default view" label in
    // place of a meaningful descriptor read as a stale debug string in the PDF.
    const datasetSuffix = isProvisional
        ? 'Provisional (management-supplied)'
        : as.hasConsolidated && as.hasStandalone
        ? 'Standalone & Consolidated'
        : as.hasConsolidated
        ? 'Consolidated'
        : as.hasStandalone
        ? 'Standalone'
        : '';
    const countLabel = `${as.statements.length} statement${as.statements.length > 1 ? 's' : ''}`;
    const subtitle = datasetSuffix ? `${countLabel} · ${datasetSuffix}` : countLabel;
    return (
        <PdfSection
            kicker="Section · Statements"
            title={isProvisional ? 'Provisional Financial Statements' : 'Audited Financial Statements'}
            subtitle={subtitle}
            index={index}
            breakBefore={breakBefore}
        >
            {/* Units chip — pinned right; tells the reader the scale of the numbers.
                Without it readers used to ask "are these crores or lakhs?". The label
                is conservative ("All amounts in ₹") because the API doesn't always
                return a unit field, so we don't want to assert lakhs/crores when we
                actually don't know. */}
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 2, marginBottom: 4 }}>
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        color: colors.muted,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        fontFamily: fontFamily.sansBold,
                    }}
                >
                    All amounts in ₹
                </Text>
            </View>
            {as.statements.map((slice, i) => (
                <View key={slice.name} style={{ marginTop: i === 0 ? 0 : space.lg }}>
                    {/* Slice name (e.g. "Balance Sheet" / "Profit & Loss"). Per Figma
                        32:100991, render in ink with sentence-case bold — NOT brand-purple
                        uppercase. The all-caps purple treatment was reading as a "section"
                        and competing with the actual section title at the top of the page.
                        Switched from sansBold (700) → sans@600 to dial down the visual weight
                        per design feedback — bold was crowding the slice numerics below. */}
                    <Text
                        style={{
                            fontSize: typeScale.h4,
                            fontFamily: fontFamily.sans,
                            fontWeight: 600,
                            color: colors.ink00,
                            marginBottom: 6,
                        }}
                    >
                        {slice.name}
                    </Text>
                    <PdfStatementTable slice={slice} />
                </View>
            ))}
        </PdfSection>
    );
};

// ──────────────────────────── Screen ────────────────────────────

const ScreenStatementTable: React.FC<{ slice: AuditedStatementSlice }> = ({ slice }) => {
    const cols = yearColumns(slice);
    const useGroups = isBalanceSheet(slice.name);
    const rawGroups = useGroups ? groupBalanceSheet(slice.rows) : { [slice.name]: slice.rows };
    const groups = Object.fromEntries(Object.entries(rawGroups).map(([k, v]) => [k, totalsLast(v)]));
    const isRatioTable = isRatios(slice.name);
    const thStyle: React.CSSProperties = {
        padding: '8px 6px',
        fontSize: 10,
        color: colors.muted,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        fontWeight: 700,
        textAlign: 'right',
        borderBottom: `1px solid ${colors.ink}`,
    };
    const tdStyle: React.CSSProperties = {
        padding: '6px',
        fontSize: 11.5,
        textAlign: 'right',
        color: colors.ink,
        borderBottom: `0.5px solid ${colors.hairlineSoft}`,
        fontVariantNumeric: 'tabular-nums',
    };
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
                <tr>
                    <th style={{ ...thStyle, textAlign: 'left', width: '32%' }}>{isRatioTable ? 'Ratio' : 'Classification'}</th>
                    {cols.map((h) => (
                        <th key={h.key} style={thStyle}>
                            {h.name}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Object.entries(groups).map(([groupName, rows]) => (
                    <React.Fragment key={groupName}>
                        {useGroups ? (
                            <tr>
                                <td
                                    colSpan={cols.length + 1}
                                    style={{
                                        padding: '6px',
                                        fontSize: 10,
                                        letterSpacing: 1,
                                        color: colors.ink00,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        background: colors.surfaceAlt,
                                    }}
                                >
                                    {groupName}
                                </td>
                            </tr>
                        ) : null}
                        {rows.map((r, i) => (
                            <tr key={i} style={{ background: r.isAggregated ? colors.surfaceAlt : undefined }}>
                                <td
                                    style={{
                                        ...tdStyle,
                                        textAlign: 'left',
                                        paddingLeft: r.subLabel ? 14 : 6,
                                        color: colors.inkSoft,
                                        fontWeight: r.isAggregated ? 600 : 400,
                                    }}
                                >
                                    {r.classificationName}
                                </td>
                                {cols.map((h) => (
                                    <td key={h.key} style={tdStyle}>
                                        {formatCell(r.values[h.key])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </React.Fragment>
                ))}
            </tbody>
        </table>
    );
};

const ScreenAuditedStatements: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const audited = data.auditedStatements;
    const provisional = data.provisionalStatements;
    const isProvisional = !audited || audited.statements.length === 0;
    const as = isProvisional ? provisional : audited;
    const [active, setActive] = useState<string>(as?.statements?.[0]?.name ?? '');
    if (!as || as.statements.length === 0) return null;
    const slice = as.statements.find((s) => s.name === active) ?? as.statements[0];
    const datasetSuffix = isProvisional
        ? 'Provisional (management-supplied)'
        : as.hasConsolidated && as.hasStandalone
        ? 'Standalone & Consolidated available'
        : as.hasConsolidated
        ? 'Consolidated only'
        : as.hasStandalone
        ? 'Standalone only'
        : '';
    const countLabel = `${as.statements.length} statement${as.statements.length > 1 ? 's' : ''}`;
    const subtitle = datasetSuffix ? `${countLabel} · ${datasetSuffix}` : countLabel;
    return (
        <ScreenSection
            kicker="Section · Statements"
            title={isProvisional ? 'Provisional Financial Statements' : 'Audited Financial Statements'}
            subtitle={subtitle}
            index={index}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    fontSize: 10,
                    color: colors.muted,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    marginTop: 2,
                    marginBottom: 4,
                }}
            >
                All amounts in ₹
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 0' }}>
                {as.statements.map((s) => {
                    const isActive = s.name === active;
                    return (
                        <button
                            key={s.name}
                            onClick={() => setActive(s.name)}
                            style={{
                                padding: '6px 12px',
                                border: `1px solid ${isActive ? colors.brand : colors.hairlineSoft}`,
                                borderRadius: 999,
                                background: isActive ? colors.brand : 'transparent',
                                color: isActive ? '#fff' : colors.inkSoft,
                                fontSize: 11.5,
                                fontWeight: 600,
                                cursor: 'pointer',
                                letterSpacing: 0.3,
                            }}
                        >
                            {s.name}
                        </button>
                    );
                })}
            </div>
            <ScreenStatementTable slice={slice} />
        </ScreenSection>
    );
};

export const auditedStatementsModule: ModuleDefinition = {
    meta: {
        id: 'auditedStatements' as any,
        title: 'Audited financial statements',
        description: 'Balance Sheet, P&L, Cashflow & Financial Ratios from audited filings.',
        dataSource: 'polus/insights/financialsAuditedDataRange',
    },
    // Module renders if EITHER audited or provisional statements are present —
    // the body falls through to provisional when audited is empty.
    hasData: (d) =>
        (!!d.auditedStatements && d.auditedStatements.statements.length > 0) ||
        (!!d.provisionalStatements && d.provisionalStatements.statements.length > 0),
    Pdf: PdfAuditedStatements,
    Screen: ScreenAuditedStatements,
};
